import React from 'react';
import CodeSnippet from './CodeSnippet';
import {
  DocContainer,
  Title,
  SubTitle,
  Paragraph,
  List,
  ListItem,
  OrderedList,
  OrderedListItem,
  Code,
} from './DocStyles';

const ComponentesDoc: React.FC = () => {
  return (
    <DocContainer>
      <Title>Sistema de Componentização no SmartEntrega</Title>

      <Paragraph>
        Este documento explica como os componentes são organizados e utilizados
        no projeto SmartEntrega, ajudando você a entender a estrutura de
        componentização e como criar novos componentes.
      </Paragraph>

      <SubTitle>Estrutura de Componentes</SubTitle>
      <Paragraph>
        No SmartEntrega, os componentes são divididos em diferentes categorias:
      </Paragraph>
      <List>
        <ListItem>
          <strong>Componentes de UI Básicos</strong>: Elementos reutilizáveis
          como botões, inputs, cards
        </ListItem>
        <ListItem>
          <strong>Componentes de Layout</strong>: Estruturas que definem o
          layout da aplicação
        </ListItem>
        <ListItem>
          <strong>Componentes de Página</strong>: Componentes que representam
          páginas inteiras
        </ListItem>
        <ListItem>
          <strong>Componentes de Container</strong>: Componentes que gerenciam
          estado e lógica
        </ListItem>
      </List>

      <SubTitle>Componentes de UI Básicos</SubTitle>
      <Paragraph>
        Estes componentes estão localizados em <Code>src/components</Code> e são
        os blocos de construção fundamentais da interface.
      </Paragraph>

      <Paragraph>
        <strong>Exemplo de Componente Básico</strong>
      </Paragraph>
      <CodeSnippet
        code={`// src/components/Button.tsx
import React from 'react';
import styled from 'styled-components';

interface ButtonProps {
  text: string;
  onClick: () => void;
  variant?: 'primary' | 'secondary' | 'danger';
  disabled?: boolean;
}

const StyledButton = styled.button<{ variant: string }>\`
  padding: 10px 15px;
  border-radius: 4px;
  font-weight: bold;
  cursor: pointer;
  border: none;
  
  /* Estilos baseados na variante */
  background-color: \${props => {
    switch (props.variant) {
      case 'primary': return '#007bff';
      case 'secondary': return '#6c757d';
      case 'danger': return '#dc3545';
      default: return '#007bff';
    }
  }};
  color: white;
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
\`;

const Button: React.FC<ButtonProps> = ({ 
  text, 
  onClick, 
  variant = 'primary', 
  disabled = false 
}) => {
  return (
    <StyledButton 
      variant={variant} 
      onClick={onClick} 
      disabled={disabled}
    >
      {text}
    </StyledButton>
  );
};

export default Button;`}
      />

      <SubTitle>Componentes de Layout</SubTitle>
      <Paragraph>
        O componente de Layout define a estrutura comum para todas as páginas
        autenticadas.
      </Paragraph>

      <Paragraph>
        <strong>Exemplo do Componente Layout</strong>
      </Paragraph>
      <CodeSnippet
        code={`// src/components/Layout.tsx
import React from 'react';
import styled from 'styled-components';
import Sidebar from './Sidebar';
import Header from './Header';

interface LayoutProps {
  children: React.ReactNode;
  pageTitle: string;
}

const LayoutContainer = styled.div\`
  display: flex;
  min-height: 100vh;
\`;

const MainContent = styled.main\`
  flex: 1;
  padding: 20px;
  background-color: #f5f5f5;
\`;

const Layout: React.FC<LayoutProps> = ({ children, pageTitle }) => {
  return (
    <LayoutContainer>
      <Sidebar />
      <div style={{ flex: 1 }}>
        <Header title={pageTitle} />
        <MainContent>
          {children}
        </MainContent>
      </div>
    </LayoutContainer>
  );
};

export default Layout;`}
      />

      <SubTitle>Componentes de Página</SubTitle>
      <Paragraph>
        Os componentes de página representam páginas inteiras da aplicação e
        estão localizados em <Code>src/pages</Code>.
      </Paragraph>

      <Paragraph>
        <strong>Exemplo de Componente de Página</strong>
      </Paragraph>
      <CodeSnippet
        code={`// src/pages/Dashboard.tsx
import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { Card, Chart, StatisticBox } from '../components';
import { fetchDashboardData } from '../services/api';

const DashboardContainer = styled.div\`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 20px;
\`;

const Dashboard: React.FC = () => {
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        const data = await fetchDashboardData();
        setDashboardData(data);
        setError(null);
      } catch (err) {
        setError('Erro ao carregar dados do dashboard');
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, []);

  if (loading) return <div>Carregando...</div>;
  if (error) return <div>Erro: {error}</div>;

  return (
    <DashboardContainer>
      <StatisticBox 
        title="Total de Entregas" 
        value={dashboardData?.totalEntregas || 0} 
        icon="truck" 
      />
      <StatisticBox 
        title="Entregas Pendentes" 
        value={dashboardData?.entregasPendentes || 0} 
        icon="clock" 
      />
      <StatisticBox 
        title="Entregas Concluídas" 
        value={dashboardData?.entregasConcluidas || 0} 
        icon="check-circle" 
      />
      
      <Card title="Entregas por Região">
        <Chart 
          type="bar" 
          data={dashboardData?.entregasPorRegiao || []} 
        />
      </Card>
      
      <Card title="Desempenho Mensal">
        <Chart 
          type="line" 
          data={dashboardData?.desempenhoMensal || []} 
        />
      </Card>
    </DashboardContainer>
  );
};

export default Dashboard;`}
      />

      <SubTitle>Componentes de Container</SubTitle>
      <Paragraph>
        Os componentes de container gerenciam estado e lógica de negócio,
        geralmente conectando-se ao Redux.
      </Paragraph>

      <Paragraph>
        <strong>Exemplo de Componente Container</strong>
      </Paragraph>
      <CodeSnippet
        code={`// src/containers/UserListContainer.tsx
import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchUsers, deleteUser } from '../store/slices/userSlice';
import { UserList } from '../components';
import { RootState, AppDispatch } from '../store';

const UserListContainer: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { users, loading, error } = useSelector((state: RootState) => state.users);

  useEffect(() => {
    dispatch(fetchUsers());
  }, [dispatch]);

  const handleDeleteUser = (userId: string) => {
    if (window.confirm('Tem certeza que deseja excluir este usuário?')) {
      dispatch(deleteUser(userId));
    }
  };

  if (loading) return <div>Carregando usuários...</div>;
  if (error) return <div>Erro: {error}</div>;

  return (
    <UserList 
      users={users} 
      onDeleteUser={handleDeleteUser} 
    />
  );
};

export default UserListContainer;`}
      />

      <SubTitle>Componentes Protegidos</SubTitle>
      <Paragraph>
        O SmartEntrega utiliza componentes especiais para proteger rotas que
        requerem autenticação.
      </Paragraph>

      <Paragraph>
        <strong>Exemplo de Componente de Rota Protegida</strong>
      </Paragraph>
      <CodeSnippet
        code={`// src/components/ProtectedRoute.tsx
import React from 'react';
import { Navigate } from 'react-router-dom';
import { useAppSelector } from '../store';

interface ProtectedRouteProps {
  children: React.ReactElement;
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children }) => {
  const { isAuthenticated } = useAppSelector((state) => state.auth);
  
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  
  return children;
};

export default ProtectedRoute;`}
      />

      <SubTitle>Como Criar um Novo Componente</SubTitle>
      <OrderedList>
        <OrderedListItem>
          <strong>Identifique o tipo de componente</strong> que você precisa
          criar (UI básico, layout, página, container)
        </OrderedListItem>
        <OrderedListItem>
          <strong>Crie um novo arquivo</strong> na pasta apropriada com extensão{' '}
          <Code>.tsx</Code>
        </OrderedListItem>
        <OrderedListItem>
          <strong>Defina a interface</strong> para as props do componente
        </OrderedListItem>
        <OrderedListItem>
          <strong>Implemente o componente</strong> usando React e Styled
          Components
        </OrderedListItem>
        <OrderedListItem>
          <strong>Exporte o componente</strong> para que possa ser usado em
          outros lugares
        </OrderedListItem>
      </OrderedList>

      <Paragraph>
        <strong>Exemplo de Criação de Componente</strong>
      </Paragraph>
      <CodeSnippet
        code={`// src/components/InfoCard.tsx
import React from 'react';
import styled from 'styled-components';

// 1. Defina a interface para as props
interface InfoCardProps {
  title: string;
  content: string;
  icon?: string;
  variant?: 'info' | 'success' | 'warning' | 'danger';
}

// 2. Crie os componentes estilizados
const CardContainer = styled.div<{ variant: string }>\`
  padding: 15px;
  border-radius: 8px;
  margin-bottom: 15px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  
  background-color: \${props => {
    switch (props.variant) {
      case 'info': return '#e8f4fd';
      case 'success': return '#e9f7ef';
      case 'warning': return '#fef9e7';
      case 'danger': return '#fdedec';
      default: return '#e8f4fd';
    }
  }};
\`;

const CardTitle = styled.h3\`
  margin-top: 0;
  font-size: 18px;
  display: flex;
  align-items: center;
\`;

const CardContent = styled.div\`
  font-size: 14px;
\`;

const Icon = styled.span\`
  margin-right: 8px;
\`;

// 3. Implemente o componente
const InfoCard: React.FC<InfoCardProps> = ({ 
  title, 
  content, 
  icon, 
  variant = 'info' 
}) => {
  return (
    <CardContainer variant={variant}>
      <CardTitle>
        {icon && <Icon>{icon}</Icon>}
        {title}
      </CardTitle>
      <CardContent>{content}</CardContent>
    </CardContainer>
  );
};

// 4. Exporte o componente
export default InfoCard;`}
      />

      <SubTitle>Boas Práticas para Componentes</SubTitle>
      <List>
        <ListItem>
          <strong>Mantenha os componentes pequenos e focados</strong> em uma
          única responsabilidade
        </ListItem>
        <ListItem>
          <strong>Use TypeScript</strong> para definir as props e garantir a
          tipagem correta
        </ListItem>
        <ListItem>
          <strong>Separe a lógica de negócio</strong> da apresentação (use
          containers quando necessário)
        </ListItem>
        <ListItem>
          <strong>Utilize Styled Components</strong> para estilização
          consistente
        </ListItem>
        <ListItem>
          <strong>Documente seus componentes</strong> com comentários
          explicativos
        </ListItem>
        <ListItem>
          <strong>Reutilize componentes</strong> sempre que possível em vez de
          duplicar código
        </ListItem>
        <ListItem>
          <strong>Nomeie seus componentes</strong> de forma clara e descritiva
        </ListItem>
        <ListItem>
          <strong>
            Divida os arquivos em arquivos de estilo e arquivos de código
          </strong>
        </ListItem>
      </List>
    </DocContainer>
  );
};

export default ComponentesDoc;
